/** An alert's structured detail, as readable fields rather than a JSON blob.
 *
 * The column used to render `JSON.stringify(detail, null, 2)` in a <pre>. That is a debugging
 * view: it shows an operator braces, quotes and camelCase key names, spreads a two-field object
 * over five lines, and makes every row in the table three times taller than its content. The
 * person reading this screen at 2am wants "Door: side-gate", not `{"doorId":"side-gate"}`.
 *
 * Every detail this service actually raises is a flat object of strings and numbers - doorId,
 * helperExitCode, consecutiveFailures, correlationId and so on - so a label/value list covers
 * all of them. Anything unexpected (a nested object, an array, a bare string) still renders,
 * compactly, rather than disappearing: an alert detail is evidence, and silently dropping a
 * field nobody anticipated is worse than showing it raw.
 */
import { formatDetailJson } from "../lib/format.js";

/** Labels for the keys this service raises. A key with no entry here is humanised by the
 * fallback below, so a new field added to an alert payload still reads sensibly on day one. */
const LABELS: Readonly<Record<string, string>> = {
  doorId: "Door",
  correlationId: "Correlation",
  helperExitCode: "Helper exit code",
  observedStatusValue: "Observed status",
  statusValue: "Status value",
  consecutiveFailures: "Consecutive failures",
  authorisedBy: "Authorised by",
  attempts: "Attempts",
  requested: "Requested",
  observed: "Observed",
};

/** `consecutiveFailures` -> `Consecutive failures`. Also copes with snake_case. */
function humanise(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Identifiers are read character by character when someone is comparing them to a log, so they
 * get the monospace face. Counts and codes do not need it. */
const MONO_KEYS = new Set(["doorId", "correlationId"]);

export function AlertDetail({ detail }: { detail: unknown }) {
  if (detail === null || detail === undefined) return <span className="cell-sub">—</span>;

  // A plain scalar detail is already a sentence; it needs no field list.
  if (typeof detail !== "object") return <span className="cell-sub">{String(detail)}</span>;

  const entries = Object.entries(detail as Record<string, unknown>).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (entries.length === 0) return <span className="cell-sub">—</span>;

  return (
    <dl className="kv">
      {entries.map(([key, value]) => {
        // A nested object or array is rare and has no label scheme of its own - show it
        // compactly rather than pretending it is a scalar.
        const nested = typeof value === "object";
        return (
          <div className="kv-row" key={key}>
            <dt>{LABELS[key] ?? humanise(key)}</dt>
            <dd className={!nested && MONO_KEYS.has(key) ? "mono" : undefined}>
              {nested ? formatDetailJson(value) : String(value)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
